/*
  Wire America -- empty search states, desktop (client-approved design, 17 Sep 2026).

  What this does, and only this: on /search, when the search returns nothing,
  it replaces the theme's dead-end "No products found." strip with the approved
  block -- what happened, how to get a person, and somewhere to click.

  What it deliberately does NOT do:
    - nothing to the header instant-search dropdown. The design set has a board
      for it, but the dropdown stays exactly as the theme ships it (Zineb,
      18 Sep): this change is the results page only.
    - no change to the Algolia index, queries, facets or ranking
    - no change to algolia_headercomplete.js or instant_search.js: both keep
      writing exactly the markup they write today. We read their output and
      layer on top, so a vendor update cannot silently break the search itself,
      only this enhancement.
    - nothing below 750px. The phone board in the same design set (results page
      plus a "Did you mean" line) was not part of the desktop sign-off and is a
      separate decision. The stylesheet is gated the same way.

  Data comes from the #wa-search-empty-data island rendered in
  snippets/header-inner.liquid, so the category list follows the Shop menu and
  the product list follows a collection the client edits in admin. No content
  is hardcoded here.
*/

(function () {
  'use strict';

  var MIN_WIDTH = 750;
  var data = null;

  function readData() {
    if (data) return data;
    var node = document.getElementById('wa-search-empty-data');
    if (!node) return null;
    try {
      data = JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
    return data;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function wide() {
    return window.innerWidth >= MIN_WIDTH;
  }

  /* --------------------------------------------------------------------
     Shared fragments
     -------------------------------------------------------------------- */

  var SEARCH_ICON =
    '<svg class="wa-se-icon" width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.6"></circle>' +
    '<path d="M20 20 L16 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>' +
    '</svg>';

  var PHONE_ICON =
    '<svg class="wa-se-cta-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M6 3 L9.5 3 L11 8 L8.7 9.7 A12 12 0 0 0 14.3 15.3 L16 13 L21 14.5 L21 18 ' +
    'A2.5 2.5 0 0 1 18.2 20.5 A17.5 17.5 0 0 1 3.5 5.8 A2.5 2.5 0 0 1 6 3 Z" ' +
    'stroke-width="1.7" stroke-linejoin="round"></path></svg>';

  function messageHtml(term, withIcon) {
    var d = readData();
    return (
      '<div class="wa-se-msg">' +
      (withIcon ? SEARCH_ICON : '') +
      '<p class="wa-se-title">No matches for &ldquo;' + esc(term) + '&rdquo;</p>' +
      '<p class="wa-se-copy">' + esc(d.copy) + '</p>' +
      '</div>'
    );
  }

  function actionsHtml() {
    var d = readData();
    var out = '<div class="wa-se-actions">';
    if (d.quoteUrl) {
      out +=
        '<a class="wa-se-cta wa-se-cta--primary" href="' + esc(d.quoteUrl) + '">' +
        esc(d.quoteLabel) + '</a>';
    }
    if (d.phone && d.phoneHref) {
      out +=
        '<a class="wa-se-cta wa-se-cta--ghost" href="' + esc(d.phoneHref) + '" ' +
        'aria-label="Call Wire America on ' + esc(d.phone) + '">' +
        PHONE_ICON + esc(d.phone) + '</a>';
    }
    return out + '</div>';
  }

  /* --------------------------------------------------------------------
     The search results page
     -------------------------------------------------------------------- */

  function isSearchPage() {
    return /^\/search(\/|$|\?)/.test(window.location.pathname + window.location.search) ||
      window.location.pathname === '/search';
  }

  // The theme submits the header field as ?query= and Shopify's own form uses
  // ?q=, and both land here. Take either, then fall back to the heading the
  // page already prints.
  function currentTerm() {
    var m = window.location.search.match(/[?&](?:q|query)=([^&]*)/);
    if (m) {
      try {
        return decodeURIComponent(m[1].replace(/\+/g, ' '));
      } catch (e) {
        return m[1];
      }
    }
    var h1 = document.querySelector('.collection-hero__title');
    if (h1) {
      var parts = h1.textContent.split(':');
      if (parts.length > 1) return parts.slice(1).join(':').trim();
    }
    return '';
  }

  function pageHtml(term) {
    var d = readData();
    var out = '<div class="wa-se wa-se-page">';

    out += '<div class="wa-se-card">' + messageHtml(term, true) + actionsHtml() + '</div>';

    if (d.categories && d.categories.length) {
      out += '<div class="wa-se-group">';
      out += '<p class="wa-se-label">Popular right now</p>';
      out += '<ul class="wa-se-pills">';
      d.categories.forEach(function (c) {
        out += '<li><a href="' + esc(c.url) + '">' + esc(c.title) + '</a></li>';
      });
      out += '</ul></div>';
    }

    if (d.products && d.products.length) {
      out += '<div class="wa-se-group">';
      out += '<div class="wa-se-grouphead">';
      out += '<p class="wa-se-label">What other customers order most</p>';
      if (d.shopAllUrl) {
        out +=
          '<a class="wa-se-seeall" href="' + esc(d.shopAllUrl) + '">' +
          esc(d.shopAllLabel) + '</a>';
      }
      out += '</div><ul class="wa-se-grid">';
      d.products.forEach(function (p) {
        out += '<li><a href="' + esc(p.url) + '">';
        if (p.image) {
          out +=
            '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" ' +
            'width="300" height="132">';
        }
        out += '<span class="wa-se-cardbody">';
        out += '<span class="wa-se-name">' + esc(p.title) + '</span>';
        if (p.quote) {
          out += '<span class="wa-se-price wa-se-price--quote">' + esc(p.price) + '</span>';
        } else {
          out +=
            '<span class="wa-se-price">' + esc(p.price) +
            (p.unit ? '<span class="wa-se-unit">' + esc(p.unit) + '</span>' : '') +
            '</span>';
        }
        out += '</span></a></li>';
      });
      out += '</ul></div>';
    }

    return out + '</div>';
  }

  // The theme builds this block with innerHTML += on the collection wrapper, so
  // the node can be replaced wholesale at any point. Re-check rather than bind.
  function syncPage() {
    if (!isSearchPage() || !wide() || !readData()) return;

    var blocks = document.querySelectorAll('.collection-empty');
    for (var i = 0; i < blocks.length; i++) {
      var el = blocks[i];
      if (el.classList.contains('hidden') || el.offsetParent === null) continue;
      if (el.querySelector('.wa-se-page')) continue;

      var term = currentTerm();
      if (!term) continue;

      el.classList.add('wa-se-on');
      el.insertAdjacentHTML('beforeend', pageHtml(term));
      return;
    }
  }

  /* --------------------------------------------------------------------
     Wiring

     The empty strip is written by instant_search.js, which fires no event, so
     we watch the DOM. The observer is the fast path; the interval is the
     safety net for the first paint, when this file may run before the results
     have been requested at all.
     -------------------------------------------------------------------- */

  function sync() {
    syncPage();
  }

  function start() {
    sync();

    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        sync();
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    var ticks = 0;
    var timer = window.setInterval(function () {
      sync();
      if (++ticks > 40) window.clearInterval(timer);
    }, 250);

    window.addEventListener('resize', sync);
  }

  if (document.body) {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
